/**
 * Аватар людини (PRF-02; files.md, розд. 3.5): фото, якщо воно є, інакше ініціали. Фото віддає `api`
 * (`GET /api/v1/users/{id}/avatar/{32|96|256}`, 5 хв кешу браузера). У адресі — `?v=<avatar>` з
 * `GET /me`: нове фото — нова адреса, тож кеш не показує старого. Легкий режим — завжди ініціали
 * (lite-mode.md): жодного запиту за фото. Фото не завантажилось — ініціали.
 */
import { useState } from 'react';
import { usePrefs } from '../prefs/prefs.ts';
import { initialsOf } from './user.ts';

/** Розміри фото, які віддає сервер (`AVATAR_SIZES`, files-storage.md, розд. 5). */
export type AvatarPx = 32 | 96 | 256;

export const avatarUrl = (userId: string, avatar: string, px: AvatarPx) =>
  `/api/v1/users/${encodeURIComponent(userId)}/avatar/${px}?v=${encodeURIComponent(avatar)}`;

/** CSS-розмір кружка → фото для 1x і 2x: менше за кружок сервер не віддає. */
const SOURCES = {
  md: [32, 96],
  lg: [96, 256],
} as const satisfies Record<string, readonly [AvatarPx, AvatarPx]>;

interface Props {
  userId: string;
  /** `avatar` з `GET /me`: `null` — фото немає. */
  avatar: string | null;
  name: string;
  /** `md` — 24 px (меню користувача), `lg` — 64 px (профіль). */
  size: keyof typeof SOURCES;
  className: string;
  /** Підпис для програми читання; без нього аватар декоративний (ім'я поруч). */
  label?: (photo: boolean) => string;
}

export function UserAvatar({ userId, avatar, name, size, className, label }: Props) {
  const lite = usePrefs().lite;
  const [broken, setBroken] = useState<string | null>(null);
  const photo = avatar !== null && !lite && broken !== avatar;
  const [x1, x2] = SOURCES[size];
  return (
    <span
      className={photo ? `${className} avatar--photo` : className}
      {...(label ? { role: 'img', 'aria-label': label(photo) } : { 'aria-hidden': true })}
    >
      {photo ? (
        <img
          className="avatar__img"
          src={avatarUrl(userId, avatar, x1)}
          srcSet={`${avatarUrl(userId, avatar, x1)} 1x, ${avatarUrl(userId, avatar, x2)} 2x`}
          alt=""
          decoding="async"
          draggable={false}
          onError={() => setBroken(avatar)}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}
